import { useQuery } from "@apollo/client/react";
import { Outlet, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  GetTrashedMediaDocument,
  type GetTrashedMediaQuery,
  type GetTrashedMediaQueryVariables,
  type MediaType,
} from "../../generated/graphql";
import { useAppSelector } from "../../store";
import { MediaTableView } from "./MediaTableView";
import { getMediaConfig } from "./config";
import { FoldersSection } from "./folders";
import { Breadcrumbs, MediaHeaderActions } from "./layout";
import { CreateFolderModal, MediaUploadModal, TextToImageModal, TextToSpeechModal } from "./modals";

interface MediaPageProps {
  /** Media type for this page */
  mediaType: MediaType;
  /** Base path for child route detection (e.g., "/media/audio") */
  basePath: string;
}

/**
 * MediaPage - Generic page component for all media types
 *
 * Handles header actions, breadcrumbs, folders, table, and modals.
 * Used by audio, images, videos, and files routes.
 */
export function MediaPage({ mediaType, basePath }: MediaPageProps) {
  const location = useLocation();
  const selectedFolderId = useAppSelector((state) => state.media.selectedFolderId);
  const config = getMediaConfig(mediaType);

  // Modal states
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showTTSModal, setShowTTSModal] = useState(false);
  const [showTTIModal, setShowTTIModal] = useState(false);

  // Search state
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Check if we're on a child route (like /trash)
  const isChildRoute = location.pathname !== basePath;

  // Query trash count
  const { data: trashData } = useQuery<GetTrashedMediaQuery, GetTrashedMediaQueryVariables>(
    GetTrashedMediaDocument,
    {
      variables: {
        type: mediaType,
        folderId: selectedFolderId,
      },
    }
  );
  const trashCount = trashData?.trashedMedia?.totalCount || 0;

  const handleSearchClear = () => {
    setSearchInput("");
    setDebouncedSearchQuery("");
  };

  const handleUpload = () => {
    setShowUploadModal(true);
  };

  const handleNewFolder = () => {
    setShowCreateFolderModal(true);
  };

  const handleGenerateAudio = () => {
    setShowTTSModal(true);
  };

  const handleGenerateImage = () => {
    setShowTTIModal(true);
  };

  // If on a child route (like /trash), just render the outlet
  if (isChildRoute) {
    return <Outlet />;
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-4 lg:p-[1%]">
          {/* Header Actions */}
          <MediaHeaderActions
            mediaType={mediaType}
            onNewFolder={handleNewFolder}
            onUpload={handleUpload}
            onGenerateAudio={handleGenerateAudio}
            onGenerateImage={handleGenerateImage}
            searchValue={searchInput}
            onSearchChange={setSearchInput}
            onSearchClear={handleSearchClear}
            trashCount={trashCount}
          />

          {/* Breadcrumbs */}
          <Breadcrumbs
            currentFolderId={selectedFolderId}
            mediaType={mediaType}
            rootLabel={config.label}
          />

          {/* Folders Section */}
          <FoldersSection
            parentId={selectedFolderId}
            mediaType={mediaType}
            searchQuery={debouncedSearchQuery}
            onClearSearch={handleSearchClear}
          />

          {/* Media Table */}
          <MediaTableView
            folderId={selectedFolderId}
            mediaType={mediaType}
            searchQuery={debouncedSearchQuery}
          />
        </div>
      </div>

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
        parentId={selectedFolderId}
        type={mediaType}
      />

      {/* Upload Modal */}
      <MediaUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        mediaType={mediaType}
        folderId={selectedFolderId}
      />

      {/* Text-to-Speech Modal */}
      <TextToSpeechModal
        isOpen={showTTSModal}
        onClose={() => setShowTTSModal(false)}
        mediaType={mediaType}
        currentFolderId={selectedFolderId}
      />

      {/* Text-to-Image Modal */}
      <TextToImageModal
        isOpen={showTTIModal}
        onClose={() => setShowTTIModal(false)}
        mediaType={mediaType}
        currentFolderId={selectedFolderId}
      />
    </>
  );
}

export default MediaPage;
